import { useState, useRef, useEffect, useCallback } from "react";

import { generateStationIntro, generateSpeech } from "../services/genaiService";
import { Station, DJPersona } from "../types";
import { triggerHealthCheck } from "../utils/healthCheck";

// Extend Window for Safari support
interface WindowWithWebKit extends Window {
  webkitAudioContext?: typeof AudioContext;
}

export const usePlayer = (
  isAutoAnnounceEnabled: boolean,
  djPersona: DJPersona,
  addToHistory: (station: Station) => void,
) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const prevStationRef = useRef<string | null>(null);
  const currentStationRef = useRef<Station | null>(null);
  const _errorTimeRef = useRef<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isAnnouncing, setIsAnnouncing] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Playback speed state with localStorage persistence
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(() => {
    const saved = localStorage.getItem("se_playback_speed");
    return saved ? parseFloat(saved) : 1;
  });

  // Ducking logic
  useEffect(() => {
    if (audioRef.current) {
      const duckedVolume = isAnnouncing ? volume * 0.1 : volume;
      audioRef.current.volume = Math.max(0, duckedVolume);
    }
  }, [volume, isAnnouncing]);

  const pendingSeekTimeRef = useRef<number | null>(null);

  // Resume playback position handler
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (pendingSeekTimeRef.current !== null) {
        console.log(
          `[usePlayer] Resuming at saved position: ${pendingSeekTimeRef.current}s`,
        );
        audio.currentTime = pendingSeekTimeRef.current;
        pendingSeekTimeRef.current = null;
      }
      setDuration(audio.duration);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    return () =>
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
  }, []);

  // Playback speed sync
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
    localStorage.setItem("se_playback_speed", playbackSpeed.toString());
  }, [playbackSpeed]);

  // Cycle through playback speeds: 1 → 1.25 → 1.5 → 2 → 1
  const cyclePlaybackSpeed = useCallback(() => {
    const speeds = [1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setPlaybackSpeed(speeds[nextIndex]);
  }, [playbackSpeed]);

  // Toggle play/pause - defined early for Media Session and playStation to use
  const togglePlay = useCallback(() => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play().catch(console.warn);
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, []);

  // Track isPlaying in a ref for Media Session handlers (avoids stale closures)
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Media Session API - Setup action handlers ONCE on mount
  useEffect(() => {
    console.log("[MediaSession] useEffect running, checking support...");
    console.log(
      "[MediaSession] navigator.mediaSession exists:",
      "mediaSession" in navigator,
    );

    if (!("mediaSession" in navigator)) {
      console.log("[MediaSession] NOT SUPPORTED - skipping setup");
      return;
    }

    console.log("[MediaSession] Setting up action handlers");

    // Play handler - directly controls the audio element
    navigator.mediaSession.setActionHandler("play", () => {
      console.log("[MediaSession] Play action triggered");
      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch((err: unknown) =>
            console.warn("[MediaSession] Play failed:", err),
          );
      }
    });

    // Pause handler - directly controls the audio element
    navigator.mediaSession.setActionHandler("pause", () => {
      console.log("[MediaSession] Pause action triggered");
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    });

    // Seek handlers for podcasts
    navigator.mediaSession.setActionHandler("seekbackward", () => {
      if (audioRef.current) {
        audioRef.current.currentTime = Math.max(
          0,
          audioRef.current.currentTime - 10,
        );
      }
    });

    navigator.mediaSession.setActionHandler("seekforward", () => {
      if (audioRef.current) {
        audioRef.current.currentTime += 30;
      }
    });

    // Stop handler
    navigator.mediaSession.setActionHandler("stop", () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
      }
    });

    // Cleanup on unmount
    return () => {
      if ("mediaSession" in navigator) {
        navigator.mediaSession.setActionHandler("play", null);
        navigator.mediaSession.setActionHandler("pause", null);
        navigator.mediaSession.setActionHandler("seekbackward", null);
        navigator.mediaSession.setActionHandler("seekforward", null);
        navigator.mediaSession.setActionHandler("stop", null);
      }
    };
  }, []); // Empty deps - setup once on mount

  // Sync playback state with Media Session (separate effect)
  useEffect(() => {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
      console.log(
        "[MediaSession] Updated playbackState:",
        isPlaying ? "playing" : "paused",
      );
    }
  }, [isPlaying]);

  // Audio Error Handling
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => {
      // Ignore errors for Creator/Custom stations as they use a separate engine
      // The main <audio> tag is just a placeholder/dummy in that mode
      if (
        currentStationRef.current?.provider === "creator" ||
        currentStationRef.current?.provider === "custom"
      ) {
        console.log(
          "[usePlayer] Ignoring dummy audio error for Creator station",
        );
        return;
      }

      console.error("[Audio Error]", audio.error);
      setIsPlaying(false);
      setIsBuffering(false);
      setLastError("Stream unavailable. Channel may be offline.");

      // Trigger background health check (with cooldown)
      if (currentStationRef.current) {
        triggerHealthCheck(
          currentStationRef.current.id,
          currentStationRef.current.streamUrl,
        );
      }
    };

    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);
    const handleCanPlay = () => setIsBuffering(false);

    audio.addEventListener("error", handleError);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, []);

  const triggerStationAnnouncement = useCallback(
    async (station: Station) => {
      if (station.country === "Local System") return;

      try {
        const introText = await generateStationIntro(station, djPersona);
        const audioBufferRaw = await generateSpeech(introText, djPersona);

        if (audioBufferRaw) {
          setIsAnnouncing(true);
          const windowWithWebKit = window as unknown as WindowWithWebKit;
          const ctx = new (
            window.AudioContext || windowWithWebKit.webkitAudioContext!
          )();
          if (ctx.state === "suspended") await ctx.resume();

          const buffer = await ctx.decodeAudioData(audioBufferRaw);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          const gainNode = ctx.createGain();
          gainNode.gain.value = 2.5;

          source.connect(gainNode);
          gainNode.connect(ctx.destination);
          source.onended = () => {
            setIsAnnouncing(false);
            ctx.close();
          };
          source.start(0);
          return introText;
        }
      } catch (err: unknown) {
        console.error("Announcement failed", err);
        setIsAnnouncing(false);
      }
      return null;
    },
    [djPersona],
  );

  const playStation = useCallback(
    (station: Station) => {
      // Track current station for error handling
      currentStationRef.current = station;

      if (audioRef.current) {
        const currentSrc = audioRef.current.src;
        const newUrl = station.streamUrl;

        console.log("[usePlayer] playStation called:", {
          stationName: station.name || station.lastEpisodeTitle,
          currentSrc: currentSrc?.slice(-50),
          newUrl: newUrl?.slice(-50),
          areEqual: currentSrc === newUrl,
          endsWithNew: currentSrc?.endsWith(newUrl),
        });

        // CRITICAL FIX: Stutter/Loop prevention
        // If the audio source is already set to the requested URL, do NOT reload it.
        // Just ensure it's playing.
        // NOTE: Only check exact match, not endsWith (prevents episode switching issues)
        if (currentSrc === newUrl) {
          console.log(
            "[usePlayer] Same URL detected, resuming instead of reloading",
          );
          if (audioRef.current.paused) {
            audioRef.current
              .play()
              .then(() => setIsPlaying(true))
              .catch((e: unknown) => console.warn("Resume failed", e));
          } else {
            setIsPlaying(true);
          }
          // IMPORTANT: Return early to prevent resetting playback position
          return;
        }

        console.log("[usePlayer] Loading new URL:", newUrl?.slice(0, 100));

        // --- New Station/Episode Logic ---

        // 1. Reset state
        setIsPlaying(false);
        audioRef.current.pause();

        // 2. Set new source
        audioRef.current.src = station.streamUrl;

        // Check for saved progress (Podcasts only)
        if (station.provider === "podcast") {
          const saved = localStorage.getItem(`se_progress_${station.id}`);
          if (saved) {
            const t = parseFloat(saved);
            if (t > 5) {
              // Only resume if > 5s played
              pendingSeekTimeRef.current = t;
            }
          }
        }

        audioRef.current.load();

        setCurrentTime(0);
        setDuration(0);

        // 3. Play
        const playPromise = audioRef.current.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
              setLastError(null); // Clear any previous errors on successful play

              if (station.country !== "Local System") {
                addToHistory(station);
              }

              // Update Media Session metadata for lock screen / notification controls
              if ("mediaSession" in navigator) {
                navigator.mediaSession.metadata = new MediaMetadata({
                  title: station.lastEpisodeTitle || station.name,
                  artist: station.country || "Stream Eater",
                  album: station.tags.join(", ") || "Live Stream",
                  artwork: station.logoUrl
                    ? [
                        {
                          src: station.logoUrl,
                          sizes: "96x96",
                          type: "image/png",
                        },
                      ]
                    : [],
                });
                navigator.mediaSession.playbackState = "playing";
              }

              if (
                isAutoAnnounceEnabled &&
                prevStationRef.current !== station.id &&
                station.provider !== "podcast"
              ) {
                setTimeout(() => triggerStationAnnouncement(station), 1000);
              }
              prevStationRef.current = station.id;
            })
            .catch((e: Error) => {
              // Abort errors are expected when switching fast. Ignore them.
              if (e.name !== "AbortError") {
                console.warn("Playback failed:", e);
                setIsPlaying(false);
              }
            });
        }
      }
    },
    [isAutoAnnounceEnabled, addToHistory, triggerStationAnnouncement],
  );

  // togglePlay is now defined earlier as useCallback for Media Session compatibility

  // USER ACTION: Seek to time (updates DOM and State)
  const handleSeek = (time: number) => {
    if (audioRef.current) {
      if (!isFinite(time)) return;
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  // SYSTEM EVENT: Only update State (prevents feedback loop)
  // This function should NOT touch audioRef.current.currentTime
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const now = audioRef.current.currentTime;
      setCurrentTime(now);
      if (audioRef.current.duration && isFinite(audioRef.current.duration)) {
        setDuration(audioRef.current.duration);
      }

      // Save progress for podcasts (throttle: save every ~5s)
      if (
        currentStationRef.current?.provider === "podcast" &&
        Math.floor(now) % 5 === 0
      ) {
        localStorage.setItem(
          `se_progress_${currentStationRef.current.id}`,
          now.toFixed(1),
        );
      }
    }
  };

  const handleSkip = (seconds: number) => {
    if (audioRef.current) {
      const newTime = audioRef.current.currentTime + seconds;
      audioRef.current.currentTime = Math.max(
        0,
        Math.min(newTime, duration || Infinity),
      );
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  return {
    audioRef,
    isPlaying,
    setIsPlaying,
    volume,
    setVolume,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    playStation,
    togglePlay,
    handleSeek,
    handleTimeUpdate,
    handleSkip,
    isAnnouncing,
    lastError,
    setLastError,
    isBuffering,
    playbackSpeed,
    setPlaybackSpeed,
    cyclePlaybackSpeed,
  };
};
